import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatNumber } from './format'

/**
 * Гаджет: таблица всего парка.
 *
 * Видимость решает сервер: менеджеру приходят только его регионы,
 * владельцу — весь парк. Сортировка — по клику на заголовок колонки,
 * повторный клик меняет направление; данные ограничены лимитом,
 * поэтому сортируем на клиенте без запроса.
 */

const COLUMNS = [
  { key: 'name',    label: 'Автомобиль', get: (r) => `${r.brand} ${r.model}` },
  { key: 'plate',   label: 'Госномер',   get: (r) => r.plate },
  { key: 'status',  label: 'Статус',     get: (r) => r.status_label },
  { key: 'region',  label: 'Регион',     get: (r) => r.region || '' },
  { key: 'year',    label: 'Год',        get: (r) => r.year ?? -1, numeric: true },
  { key: 'color',   label: 'Цвет',       get: (r) => r.color || '' },
  { key: 'mileage', label: 'Пробег',     get: (r) => r.mileage ?? 0, numeric: true },
]

const STATUS_TONE = {
  available: 'success',
  in_use: 'accent',
  maintenance: 'warning',
  inspection_required: 'info',
  blocked: 'danger',
}

export function FleetTableWidget({ data }) {
  const navigate = useNavigate()
  const [sortKey, setSortKey] = useState('name')
  const [dir, setDir] = useState(1) // 1 asc, -1 desc

  const toggle = (key) => {
    if (key === sortKey) setDir((d) => -d)
    else { setSortKey(key); setDir(1) }
  }

  const rows = useMemo(() => {
    const col = COLUMNS.find((c) => c.key === sortKey)
    const sorted = [...(data.rows || [])]
    sorted.sort((a, b) => {
      const x = col.get(a); const y = col.get(b)
      if (col.numeric) return (x - y) * dir
      return String(x).localeCompare(String(y), 'ru') * dir
    })
    return sorted
  }, [data.rows, sortKey, dir])

  if (!data.rows?.length) {
    return (
      <p className="widget-empty">
        {data.scope === 'manager'
          ? 'В ваших регионах нет машин'
          : 'Машины ещё не заведены'}
      </p>
    )
  }

  return (
    <div className="fleettable">
      <table className="fleettable__table">
        <caption className="visually-hidden">
          Автопарк: {data.total} машин, сортировка по колонкам
        </caption>
        <thead>
          <tr>
            {COLUMNS.map((c) => (
              <th key={c.key} scope="col"
                className={c.numeric ? 'fleettable__num' : undefined}
                aria-sort={
                  sortKey === c.key
                    ? (dir === 1 ? 'ascending' : 'descending')
                    : undefined
                }>
                <button type="button" className="fleettable__sort"
                  onClick={() => toggle(c.key)}>
                  {c.label}
                  {sortKey === c.key && (
                    <span aria-hidden="true">{dir === 1 ? '↑' : '↓'}</span>
                  )}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <th scope="row">
                <button type="button" className="fleettable__link"
                  onClick={() => navigate(`/admin/cars/${r.id}`)}>
                  {r.brand} {r.model}
                </button>
              </th>
              <td><span className="fleettable__plate">{r.plate}</span></td>
              <td>
                <span className={`fleettable__status fleettable__status--${STATUS_TONE[r.status] || 'neutral'}`}>
                  {r.status_label}
                </span>
              </td>
              <td>{r.region || '—'}</td>
              <td className="fleettable__num">{r.year ?? '—'}</td>
              <td>{r.color || '—'}</td>
              <td className="fleettable__num">
                {r.mileage == null ? '—' : `${formatNumber(r.mileage)} км`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.truncated && (
        <p className="fleettable__note">
          Показаны первые {data.rows.length} из {data.total} — полный список
          на странице «Машины»
        </p>
      )}
    </div>
  )
}
